import { useEffect, useMemo, useRef, useState } from "react";

import { toDateInputValue } from "../lib/calendarGrid";
import {
  SNOOZE_PRESETS,
  formatSnoozeDeadline,
  resolveCustomSnooze,
  type CustomSnoozeError,
} from "../lib/threadInboxState";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "./ui/dialog";
import { DateTimePicker } from "./ui/date-time-picker";
import { cn } from "../lib/utils";

interface SnoozeThreadDialogProps {
  open: boolean;
  threadTitle: string;
  onOpenChange: (open: boolean) => void;
  /** Receives an absolute instant, already resolved against this browser's clock. */
  onConfirm: (until: Date) => Promise<void> | void;
}

const CUSTOM_ERROR_MESSAGES: Record<CustomSnoozeError, string> = {
  incomplete: "Indica la fecha y la hora.",
  unparseable: "Esa fecha no existe.",
  past: "Esa hora ya pasó. Elige un momento futuro.",
};

const toTimeInputValue = (date: Date): string =>
  `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;

/**
 * Choosing how long to put a thread aside.
 *
 * The presets cover the common cases and the custom fields cover everybody
 * else's day — which is the point: "tomorrow morning" means 9:00 to the person
 * who wrote the preset and something else entirely to someone who starts at 6.
 * A fixed menu of four durations quietly assumes one schedule.
 *
 * Every option resolves here, in the browser, and travels to the server as an
 * absolute instant. The server does not know the reader's timezone and must not
 * guess it.
 */
export function SnoozeThreadDialog({
  open,
  threadTitle,
  onOpenChange,
  onConfirm,
}: SnoozeThreadDialogProps) {
  const [customDate, setCustomDate] = useState("");
  const [customTime, setCustomTime] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const firstPresetRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      setIsSaving(false);
      return;
    }
    // Seeded with tomorrow at 09:00 rather than left blank: it gives the date
    // and time fields a shape to edit instead of two empty boxes, and it is the
    // answer people most often want when the presets did not fit.
    const seed = new Date();
    seed.setDate(seed.getDate() + 1);
    seed.setHours(9, 0, 0, 0);
    setCustomDate(toDateInputValue(seed));
    setCustomTime(toTimeInputValue(seed));
    setSubmitError(null);
    setShowCustom(false);
    const frame = window.requestAnimationFrame(() => firstPresetRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  // Resolved on every keystroke so the preview and the disabled state agree with
  // what pressing the button would actually do.
  const custom = useMemo(
    () => resolveCustomSnooze({ date: customDate, time: customTime, nowMs: Date.now() }),
    [customDate, customTime],
  );

  const commit = async (until: Date) => {
    if (isSaving) return;
    setIsSaving(true);
    setSubmitError(null);
    try {
      await onConfirm(until);
      onOpenChange(false);
    } catch {
      setSubmitError("No se pudo posponer el hilo. Inténtalo de nuevo.");
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup className="max-w-md">
        <DialogHeader>
          <DialogTitle>Posponer hilo</DialogTitle>
          <DialogDescription>
            Sale de tu lista y vuelve solo. Si el hilo necesita algo de ti antes —una aprobación o
            una respuesta— reaparece igualmente.
          </DialogDescription>
        </DialogHeader>

        <DialogPanel className="flex flex-col gap-3">
          <p className="truncate text-[length:var(--app-font-size-ui,12px)] text-muted-foreground">
            {threadTitle}
          </p>

          <div className="flex flex-col gap-1.5">
            {SNOOZE_PRESETS.map((preset, index) => {
              const resolved = preset.resolve(new Date());
              return (
                <button
                  key={preset.id}
                  ref={index === 0 ? firstPresetRef : undefined}
                  type="button"
                  disabled={isSaving}
                  onClick={() => void commit(resolved)}
                  className={cn(
                    "flex items-baseline justify-between gap-3 rounded-lg border border-border/70 px-3 py-2 text-left",
                    "transition-colors hover:bg-accent/55 focus-visible:bg-accent/55 disabled:opacity-55",
                  )}
                >
                  <span className="text-[length:var(--app-font-size-ui,13px)]">{preset.label}</span>
                  {/* The preset names a rule; this says what it works out to today. */}
                  <span className="shrink-0 text-[length:var(--app-font-size-ui,11px)] text-muted-foreground/72">
                    {formatSnoozeDeadline(resolved.getTime(), Date.now())}
                  </span>
                </button>
              );
            })}
          </div>

          {showCustom ? (
            <div className="flex flex-col gap-2 rounded-lg border border-border/70 px-3 py-2.5">
              <span className="text-[length:var(--app-font-size-ui,12px)] text-muted-foreground">
                Elegir fecha y hora
              </span>
              {/* Drawn by the app, not by the operating system: the native
                  date field shows a different order, language and popup on
                  every machine, and this control's only job is to be
                  unambiguous about when the thread comes back. */}
              <DateTimePicker
                date={customDate}
                time={customTime}
                disabled={isSaving}
                onDateChange={setCustomDate}
                onTimeChange={setCustomTime}
                minDate={new Date()}
              />
              {/* Only once something has been typed: telling somebody their entry
                  is incomplete before they have started is noise, not help. */}
              {!custom.ok && custom.error !== "incomplete" ? (
                <span className="text-[length:var(--app-font-size-ui,11px)] text-destructive">
                  {CUSTOM_ERROR_MESSAGES[custom.error]}
                </span>
              ) : null}
              {custom.ok ? (
                <span className="text-[length:var(--app-font-size-ui,11px)] text-muted-foreground/72">
                  Volverá {formatSnoozeDeadline(custom.date.getTime(), Date.now())}
                </span>
              ) : null}
            </div>
          ) : (
            <button
              type="button"
              disabled={isSaving}
              onClick={() => setShowCustom(true)}
              className="self-start text-[length:var(--app-font-size-ui,12px)] text-muted-foreground underline-offset-2 hover:underline disabled:opacity-55"
            >
              Elegir otra fecha y hora…
            </button>
          )}

          {submitError ? (
            <span className="text-[length:var(--app-font-size-ui,11px)] text-destructive">
              {submitError}
            </span>
          ) : null}
        </DialogPanel>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancelar
          </Button>
          {showCustom ? (
            <Button
              onClick={() => {
                if (custom.ok) void commit(custom.date);
              }}
              disabled={!custom.ok || isSaving}
            >
              {isSaving ? "Posponiendo…" : "Posponer"}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
